import { z } from "zod";
import { paymentRequirementSchema } from "./shared";

export const serviceRequestStatusSchema = z.enum([
  "PENDING",
  "QUOTED",
  "CLOSED",
  "APPOINTMENT_SCHEDULED",
]);

export const createServiceRequestSchema = z.object({
  userId: z.string().min(1),
  salonId: z.string().min(1),
  firstName: z.string().min(1),
  lastName: z.string().min(1),
  email: z.string().email(),
  phoneNumber: z.string().min(1).optional(),
  note: z.string().optional(),
  userPhotos: z.array(z.string()).default([]),
  referencePhotos: z.array(z.string()).default([]),
  serviceIds: z.array(z.string()).default([]),
  serviceTitles: z.array(z.string()).default([]),
});

export const updateServiceRequestSchema = z.object({
  requestId: z.string().min(1),
  status: serviceRequestStatusSchema.optional(),
  quotePrice: z.number().nonnegative().optional(),
  quoteDuration: z.number().int().positive().optional(),
  quoteServiceTitle: z.string().min(1).max(200).optional(),
  quoteScheduleId: z.string().min(1).optional(),
  quoteTeamMemberId: z.string().min(1).optional(),
  closeReason: z.string().optional(),
  appointmentId: z.string().optional(),
});

export const listServiceRequestsQuerySchema = z.object({
  userId: z.string().min(1),
  // This deprecated general-purpose endpoint is tenant-authorized. Requiring
  // the tenant prevents callers from omitting it to bypass the requests grant.
  salonId: z.string().min(1),
  status: serviceRequestStatusSchema.optional(),
});

export const serviceRequestIdParamsSchema = z.object({
  requestId: z.string().min(1),
});

const timestampSchema = z.union([z.string(), z.date()]);

const serviceRequestUserSchema = z
  .object({
    id: z.string(),
    name: z.string().nullable().optional(),
  })
  .partial({ name: true });

const serviceRequestSalonSchema = z
  .object({
    id: z.string(),
    name: z.string().nullable().optional(),
  })
  .partial({ name: true });

const serviceRequestCustomerSchema = z
  .object({
    id: z.string(),
    firstName: z.string().nullable().optional(),
    lastName: z.string().nullable().optional(),
    phoneNumber: z.string().nullable().optional(),
  })
  .partial({ firstName: true, lastName: true, phoneNumber: true });

export const serviceRequestSchema = z.object({
  id: z.string(),
  userId: z.string(),
  salonId: z.string(),
  customerId: z.string().nullable().optional(),
  firstName: z.string(),
  lastName: z.string(),
  email: z.string().email().nullable().optional(),
  phoneNumber: z.string().nullable().optional(),
  note: z.string().nullable().optional(),
  userPhotos: z.array(z.string()),
  referencePhotos: z.array(z.string()),
  serviceIds: z.array(z.string()),
  serviceTitles: z.array(z.string()),
  status: serviceRequestStatusSchema,
  quotePublicId: z.string().nullable().optional(),
  quoteServiceTitle: z.string().nullable().optional(),
  quoteScheduleId: z.string().nullable().optional(),
  quoteTeamMemberId: z.string().nullable().optional(),
  quotePrice: z.number().nullable().optional(),
  quotePaymentRequirement: paymentRequirementSchema.nullable().optional(),
  quoteDepositAmount: z.number().nonnegative().nullable().optional(),
  quoteDuration: z.number().nullable().optional(),
  quoteSentAt: timestampSchema.nullable().optional(),
  /**
   * Delivery status of the current quote revision's send (Oct 2026):
   * null when none was recorded; absent from older APIs.
   */
  quoteDelivery: z
    .object({
      channel: z.enum(["SMS", "EMAIL"]),
      status: z.enum(["SENT", "DELIVERED", "OPENED", "FAILED"]),
      statusUpdatedAt: timestampSchema.nullable(),
      failureDetail: z.string().nullable(),
    })
    .nullable()
    .optional(),
  closedAt: timestampSchema.nullable().optional(),
  closeReason: z.string().nullable().optional(),
  appointmentId: z.string().nullable().optional(),
  createdAt: timestampSchema,
  updatedAt: timestampSchema,
  user: serviceRequestUserSchema.nullable().optional(),
  salon: serviceRequestSalonSchema.nullable().optional(),
  customer: serviceRequestCustomerSchema.nullable().optional(),
});

// ---------------------------------------------------------------------------
// Quote flows
// ---------------------------------------------------------------------------

export const sendServiceRequestQuoteSchema = z.object({
  requestId: z.string().min(1),
  quoteServiceId: z.string().min(1),
  quoteServiceTitle: z.string().min(1).max(200),
  quotePrice: z.number().nonnegative().optional(),
  quoteDuration: z
    .number()
    .int()
    .positive()
    .max(8 * 60),
  quoteScheduleId: z.string().min(1).optional(),
  quoteNote: z.string().max(500).optional(),
});

export const sendServiceRequestQuoteResponseSchema = z.object({
  quotePublicId: z.string().min(1),
  serviceRequest: serviceRequestSchema,
  /** False when the quote revision committed but email delivery failed. */
  emailSent: z.boolean().optional(),
});

// ---------------------------------------------------------------------------
// Direct quotes (staff-initiated, no prior ServiceRequest)
// ---------------------------------------------------------------------------
//
// Creates a new ServiceRequest with status=QUOTED and sends the quote email
// in one step. Used when the business wants to email a quote to a customer
// who hasn't submitted a request themselves.

export const sendDirectServiceRequestQuoteSchema = z.object({
  salonId: z.string().min(1),
  firstName: z.string().min(1).max(100),
  lastName: z.string().min(1).max(100),
  email: z.string().email(),
  quoteTeamMemberId: z.string().min(1),
  quoteScheduleId: z.string().min(1).optional(),
  serviceIds: z.tuple([z.string().min(1)]),
  // Compatibility/display hints only. Quote writers derive the persisted
  // titles from the validated member-service rows and apply the same cap.
  serviceTitles: z.tuple([z.string().min(1).max(200)]),
  quoteServiceTitle: z.string().min(1).max(200),
  quotePrice: z.number().nonnegative().optional(),
  quoteDuration: z
    .number()
    .int()
    .positive()
    .max(8 * 60),
  quoteNote: z.string().max(500).optional(),
});

export type SendDirectServiceRequestQuote = z.infer<
  typeof sendDirectServiceRequestQuoteSchema
>;

// ---------------------------------------------------------------------------
// Channel-aware quote issuance (Oct 2026)
// ---------------------------------------------------------------------------
//
// The operator picks Email or Text per send. These contracts belong only to
// the `/channel` routes (`.../quote/channel`, `.../direct-quote/channel`,
// and their staff twins). An older API does not know those paths, so a new
// client selecting Text gets a 404 before any mutation instead of a silent
// email. The legacy request and response schemas above are unchanged and keep
// validating the `/exact` and legacy routes.

export const quoteChannelSchema = z.enum(["EMAIL", "SMS"]);

export const sendServiceRequestQuoteWithChannelSchema =
  sendServiceRequestQuoteSchema.extend({
    channel: quoteChannelSchema,
  });

/**
 * Direct quote with a channel: email and phone are each optional, at least
 * one is required, and the chosen channel must have its address. The API
 * normalises `phoneNumber` to E.164 on write.
 */
export const sendDirectServiceRequestQuoteWithChannelSchema =
  sendDirectServiceRequestQuoteSchema
    .extend({
      email: z.string().email().optional(),
      phoneNumber: z.string().min(1).max(30).optional(),
      channel: quoteChannelSchema,
    })
    .superRefine((value, ctx) => {
      if (!value.email && !value.phoneNumber) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["email"],
          message: "Enter an email or a phone number",
        });
      }
      if (value.channel === "EMAIL" && !value.email) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["email"],
          message: "No email address for this quote",
        });
      }
      if (value.channel === "SMS" && !value.phoneNumber) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["phoneNumber"],
          message: "No phone number for this quote",
        });
      }
    });

export const sendServiceRequestQuoteWithChannelResponseSchema =
  sendServiceRequestQuoteResponseSchema.extend({
    channel: quoteChannelSchema,
    /** False when the quote revision committed but the send failed or was skipped. */
    sent: z.boolean(),
  });

export type QuoteChannel = z.infer<typeof quoteChannelSchema>;
export type SendServiceRequestQuoteWithChannel = z.infer<
  typeof sendServiceRequestQuoteWithChannelSchema
>;
export type SendDirectServiceRequestQuoteWithChannel = z.infer<
  typeof sendDirectServiceRequestQuoteWithChannelSchema
>;
export type SendServiceRequestQuoteWithChannelResponse = z.infer<
  typeof sendServiceRequestQuoteWithChannelResponseSchema
>;

// Public-safe quote payload — no PII like email/phone.
export const serviceRequestQuotePublicSchema = z.object({
  requestId: z.string().min(1),
  salonId: z.string().min(1),
  status: serviceRequestStatusSchema,
  quotePublicId: z.string().min(1),
  quoteServiceId: z.string().min(1),
  quoteServiceTitle: z.string().min(1),
  quoteScheduleId: z.string().min(1),
  // A still-open quote always supplies an active team-member schedule for
  // openings and booking. It equals quoteScheduleId when the quote stores a
  // member schedule, or is that member's unique child when quoteScheduleId
  // stores a business parent. Accepted history may retain the stored schedule.
  // Nullable/optional remains for older responses.
  bookingScheduleId: z.string().min(1).nullable().optional(),
  /**
   * The zone the API books `bookingScheduleId` in (`resolveScheduleTimezone`:
   * the parent location's zone first). The customer booking window's last
   * day is counted in it. Optional for older responses.
   */
  bookingTimeZone: z.string().optional(),
  /**
   * The quoted service's minimum notice in minutes (its business service's
   * value for a member clone); null means none. Openings and the quote
   * create apply it; the quote page shows `booking.service.minNoticeNote`.
   * Optional for older responses.
   */
  minBookingNoticeMinutes: z.number().int().positive().nullable().optional(),
  quoteTeamMemberId: z.string().min(1),
  quoteTeamMemberName: z.string().nullable().optional(),
  quotePrice: z.number().nonnegative(),
  quoteDuration: z.number().int().positive(),
  quoteSentAt: timestampSchema.nullable().optional(),
  /**
   * Authoritative acceptance state derived from the linked appointment. A
   * claimed quote can be PAYMENT_REVIEW before its processor outcome settles;
   * clients must suppress the picker without presenting that as confirmed.
   */
  bookingState: z
    .enum(["AVAILABLE", "CONFIRMED", "PAYMENT_REVIEW", "CONSUMED"])
    .optional(),
  // Immutable upfront-payment terms snapshotted when this quote link was
  // issued. `quoteDepositAmount` is already clamped to `quotePrice`; legacy
  // rows without a snapshot are exposed as NONE. Optional so older API builds
  // still validate during rolling deployments.
  quotePaymentRequirement: paymentRequirementSchema.optional(),
  quoteDepositAmount: z.number().nonnegative().nullable().optional(),
  // Compatibility alias for clients deployed before `bookingState`. New
  // consumers must use PAYMENT_REVIEW to distinguish an unsettled claim from
  // a confirmed booking.
  paymentPending: z.boolean().optional(),
});

export const serviceRequestQuotePublicResponseSchema = z.object({
  quote: serviceRequestQuotePublicSchema,
});

export const serviceRequestResponseSchema = z.object({
  serviceRequest: serviceRequestSchema,
});

export const serviceRequestListResponseSchema = z.object({
  serviceRequests: z.array(serviceRequestSchema),
});

export const deleteServiceRequestResponseSchema = z.object({
  success: z.boolean(),
});

export type ServiceRequestStatus = z.infer<typeof serviceRequestStatusSchema>;
export type CreateServiceRequest = z.infer<typeof createServiceRequestSchema>;
export type UpdateServiceRequest = z.infer<typeof updateServiceRequestSchema>;
export type SendServiceRequestQuote = z.infer<
  typeof sendServiceRequestQuoteSchema
>;
export type SendServiceRequestQuoteResponse = z.infer<
  typeof sendServiceRequestQuoteResponseSchema
>;
export type ServiceRequestQuotePublic = z.infer<
  typeof serviceRequestQuotePublicSchema
>;
export type ServiceRequestQuotePublicResponse = z.infer<
  typeof serviceRequestQuotePublicResponseSchema
>;
export type ListServiceRequestsQuery = z.infer<
  typeof listServiceRequestsQuerySchema
>;
export type ServiceRequestIdParams = z.infer<
  typeof serviceRequestIdParamsSchema
>;
export type ServiceRequest = z.infer<typeof serviceRequestSchema>;
export type ServiceRequestResponse = z.infer<
  typeof serviceRequestResponseSchema
>;
export type ServiceRequestListResponse = z.infer<
  typeof serviceRequestListResponseSchema
>;
export type DeleteServiceRequestResponse = z.infer<
  typeof deleteServiceRequestResponseSchema
>;
